import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import Button from './ui/Button';
import useTranslation from '../hooks/useTranslation';
import { messageFrom, statusMessageKey } from '../utils/apiError';

// Router-level fallback (RR's `errorElement`). `lazyWithRetry` in
// routes/index.jsx already auto-reloads once on a chunk-load failure, so
// anything reaching this component is either a second consecutive chunk
// failure (real outage, not a stale-cache blip) or an unrelated render error.
//
// It used to print one fixed English sentence — "Please refresh the page" — for
// every cause alike, so an ordinary 403 looked identical to a genuine crash and
// told the user nothing they could act on. Now it says what actually happened
// where that is knowable, and keeps the technical detail for development.
//
// Safe to use hooks here: App.js mounts LanguageProvider *above* the router, so
// this element renders inside the language context.
export default function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  // A status can arrive three ways: a Response thrown by a loader, an axios
  // rejection that escaped a component, or not at all (a plain render error).
  const status = isRouteErrorResponse(error)
    ? error.status
    : (error?.response?.status || error?.status);

  // A message the server bothered to send beats any generic wording.
  // messageFrom unwraps the {error, detail: {detail}, status} envelope that
  // modules.common.exceptions.custom_exception_handler produces.
  const serverMessage =
    messageFrom(error?.response?.data)
    || (isRouteErrorResponse(error) ? messageFrom(error.data) : '');

  const message = serverMessage || (status ? t(statusMessageKey(status)) : '');

  return (
    <div className="flex flex-col items-center justify-center h-screen text-center px-4">
      <h1 className="text-xl font-semibold mb-2">{t('common.something_went_wrong')}</h1>

      <p className="text-gray-600 mb-4 max-w-xl">
        {message || t('common.error')}
      </p>

      <Button onClick={() => window.location.reload()}>
        {t('common.refresh')}
      </Button>

      {process.env.NODE_ENV !== 'production' && (
        <details className="mt-8 max-w-2xl w-full text-left text-xs text-gray-500">
          {/* Dev-only debugging affordance, deliberately not translated. */}
          <summary className="cursor-pointer">Technical detail (development only)</summary>
          <pre className="whitespace-pre-wrap break-words mt-2">
            {status ? `HTTP ${status}\n` : ''}
            {error?.message || String(error)}
          </pre>
        </details>
      )}
    </div>
  );
}
